/**
 * Former Firebase Remote Config key: `global_remote_config` / `global_remote_config_dev` / etc.
 * (selected via APP_REMOTE_CONFIG). Values are now maintained in code.
 *
 * Environment-specific URLs (update per env or override via APP_BASE_URL where noted):
 * - common.custom_base_api_url
 * - common.app_data.share_link
 * - authentication.qr_settings.active_web_url
 * - platforms.*.on_boarding / landing image URLs
 *
 * NOTE: `feature_flags.settings.is_about_us_enabled` and `platforms.web.gtm_id` /
 * `platforms.web.ui_settings` are NOT present in the original Firebase JSON payload.
 * They were added here only because `GConfig` (config.ts) declares them as required.
 * Update these to real values when available.
 */

export const remoteConfig = {
  common: {
    default_language: 'en',
    supported_languages: ['en', 'es'],
    authentication_type: 'katon_cognito',
    subscription_type: 'inplayer',
    cms_type: 'jwplayer',
    media_provider: 'jwplayer',
    is_client_active: true,
    custom_base_api_url: 'https://dev-play.katon360.com/api/v1',
    legal_docs: {
      privacy_policy: {
        en: 'https://www.katon360.com/legal/',
        es: 'https://www.katon360.com/legal/',
      },
      terms_of_use: {
        en: 'https://www.katon360.com/legal/',
        es: 'https://www.katon360.com/legal/',
      },
    },
    app_data: {
      share_link: 'https://dev-play.katon360.com/m/',
      support_email: 'https://www.katon360.com/contact-us',
      support_email_type: 'web',
    },
  },
  inplayer: {
    drm_policy_id: '',
  },
  jwplayer: {
    config_id: import.meta.env.CONFIG_ID,
  },
  authentication: {
    email_regex:
      "^(?!.*\\.\\.)[a-zA-Z0-9](?:[a-zA-Z0-9._%+'\\-]{0,63}[a-zA-Z0-9])?@(?:[a-zA-Z0-9-]{1,63}\\.)+[a-zA-Z]{2,}$",
    is_authentication_enabled: true,
    is_qr_authentication_enabled: true,
    qr_settings: {
      show_registration_code: true,
      qr_refersh_code_time: 60,
      show_qr_image: true,
      active_web_url: 'https://d2n89vn81h98hj.cloudfront.net/activate',
      qr_image_url:
        'data:image/png;base64,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',
      qr_code_type: 'dynamic',
    },
    can_show_signin_button: false,
    can_show_signup_button: false,
    is_guest_mode_enabled: true,
    is_social_login_enabled: false,
    social_login_providers: {
      google: true,
      facebook: false,
      twitter: false,
      apple: true,
    },
    can_show_mobile_field: false,
  },
  subscription: {
    product_id_keys: {
      staging: 'productIdsStaging',
      production: 'productIds',
    },
  },
  feature_flags: {
    is_onboarding_screen_enabled: true,
    guest_streaming_enabled: false,
    ad_enabled: true,
    ad_settings: {
      ad_config_level: 'global',
      ad_delivery_provider: 'jwp',
      ad_delivery_method: 'ssai',
      ad_targeting_keys: {
        video: 'tag',
        user_type: 'NA',
        platform: 'NA',
      },
    },
    is_subscription_enabled: false,
    is_search_enabled: true,
    is_favorite_enabled: true,
    is_continue_watching_enabled: true,
    is_recommended_content_enabled: true,
    is_download_enabled: true,
    is_drm_enabled: true,
    drm_settings: {
      drm_config_id: 'string',
      drm_live_media_id: 'string',
      drm_playlist_id: 'string',
      drm_policy_id: 'string',
    },
    player_analytics_enabled: true,
    is_analytics_enabled: true,
    analytics_provider: {
      firebase: true,
      adobe: false,
      appsflyer: false,
      google: true,
    },
    is_crash_analytics: true,
    is_epg_enabled: true,
    epg_disabled_message: 'This feature is not available in this Device',
    epg_settings: {
      max_days: 1,
      max_rows: 6,
      epg_no_program_message: 'No broadcast available',
      epg_empty_slot_message: 'No scheduled programs',
      epg_time_format: '12h',
      epg_display_hours: ['00:00', '23:59'],
    },
    is_detailspage_autoplay_enabled: 'false',
    settings: {
      is_privacy_policy_enabled: true,
      is_terms_of_use_enabled: true,
      // NOT present in Firebase JSON — added because FeatureSettings requires it (config.ts)
      is_about_us_enabled: true,
      is_switch_profile_enabled: false,
      is_settings_enable: true,
      is_stream_over_wifi_enabled: true,
      is_notification_toggle_enabled: true,
      is_video_quality_enabled: true,
      is_autoplay_enabled: true,
      is_powered_by_enabled: false,
      analytics_settings: {
        abobe: {
          script_tag: 'https://assets.adobedtm.com/d421efd4183b/30aedd3f2a5f/launch-d57d7fc85e2e.min.js',
        },
      },
    },
    is_support_email: true,
    is_multi_language_support: false,
    is_google_captcha_enabled: false,
    upload_settings: {
      is_upload_enabled: true,
      is_reel_upload_enabled: true,
      is_video_upload_enabled: true,
      is_moderate_content_enabled: true,
      is_auto_approval_enabled: false,
      multipart_upload_chunk_size: 15,
      thumbnail_size: 50,
    },
    is_custom_player_enabled: false,
  },
  ui_settings: {
    hero_banner: 'option1',
    hero_carousel: 'large',
    ui_flags: {
      is_splitup_fullname: true,
      is_lastname_required: true,
    },
  },
  theme_colors: {
    primary: '#FFA823',
    bg_dark: '#050819',
    bg_light: '#FFFFFF',
    background: '#050819',
    neutral_900: '#1E2130',
    neutral_800: '#373947',
    neutral_700: '#50525E',
    neutral_600: '#696B75',
    neutral_500: '#82838C',
    neutral_400: '#9B9CA3',
    neutral_300: '#B4B5BA',
    neutral_200: '#CDCED1',
    neutral_white: '#FFFFFF',
    neutral_black: '#050819',
    alert_error: '#EF4444',
    alert_warning: '#FB923C',
    alert_success: '#22C55E',
    neutral_alpha_8: '#FFFFFF14',
    neutral_alpha_10: '#FFFFFF1A',
    neutral_alpha_20: '#FFFFFF33',
    neutral_alpha_30: '#FFFFFF4D',
    neutral_alpha_40: '#FFFFFF66',
    neutral_alpha_50: '#FFFFFF80',
    neutral_alpha_55: '#0000008C',
    neutral_alpha_60: '#FFFFFF99',
    neutral_alpha_70: '#FFFFFFB2',
    neutral_alpha_80: '#FFFFFFCC',
    transparent: '#00000000',
    image_overlay_text: '#FFFFFF',
    button_text: '#111D5E',
    primary_gradient: '#FFB647',
    primary_border: '#FFD799',
    primary_warning_color: '#EE2B2B',
    secondry_warning_color: '#FF4B4B',
  },
  platforms: {
    web: {
      is_client_active: true,
      latest_version: '1.0',
      is_share_enabled: true,
      activate_tv: false,
      export_account: true,
      delete_account: true,
      change_password: true,
      can_show_signin_button: true,
      can_show_signup_button: true,
      is_social_login_enabled: true,
      is_autoplay_enabled: true,
      player_type: 'default',
      // NOT present in Firebase JSON — added because PlatformConfig requires them (config.ts)
      gtm_id: '',
      ui_settings: {
        hero_banner: 'option1',
        ui_flags: {
          is_splitup_fullname: true,
          is_lastname_required: true,
        },
        hero_carousel: 'large',
      },
      feature_flags: {
        ad_settings: {
          ad_delivery_method: 'ssai',
        },
        is_custom_player_enabled: false,
      },
    },
    samsung: {
      is_client_active: true,
      latest_version: '1.0',
      force_update: true,
      show_buy_button: true,
      show_buy_button_alert: true,
      disable_buy_button: false,
      subscription_alert_msg: 'Please subscribe the item in Web',
      activate_tv: true,
      export_account: true,
      delete_account: true,
      change_password: true,
      player_type: 'default',
      feature_flags: {
        ad_settings: {
          ad_delivery_method: 'ssai',
        },
      },
    },
    lg: {
      is_client_active: true,
      latest_version: '1.0',
      force_update: true,
      show_buy_button: true,
      show_buy_button_alert: true,
      disable_buy_button: false,
      subscription_alert_msg: 'Please subscribe the item in Web',
      activate_tv: true,
      export_account: true,
      delete_account: true,
      change_password: true,
      player_type: 'default',
    },
    vizio: {
      is_client_active: true,
      latest_version: '1.0',
      force_update: true,
      show_buy_button: true,
      show_buy_button_alert: true,
      disable_buy_button: false,
      subscription_alert_msg: 'Please subscribe the item in Web',
      activate_tv: true,
      export_account: true,
      delete_account: true,
      change_password: true,
      player_type: 'default',
    },
    android_mobile: {
      is_client_active: true,
      latest_version: '1.0',
      force_update: true,
      show_buy_button: true,
      show_buy_button_alert: false,
      disable_buy_button: false,
      subscription_alert_msg: 'Please subscribe the item in Web',
      is_share_enabled: true,
      activate_tv: false,
      export_account: false,
      delete_account: true,
      change_password: true,
      show_tags: false,
      player_type: 'default',
    },
    android_tv: {
      is_client_active: true,
      latest_version: '1.0',
      force_update: true,
      show_buy_button: true,
      show_buy_button_alert: false,
      disable_buy_button: false,
      subscription_alert_msg: 'Please subscribe the item in Web',
      activate_tv: true,
      export_account: true,
      delete_account: true,
      change_password: true,
      player_type: 'custom',
    },
    fire_tv: {
      is_client_active: true,
      latest_version: '1.0',
      force_update: true,
      show_buy_button: true,
      show_buy_button_alert: false,
      disable_buy_button: false,
      subscription_alert_msg: 'Please subscribe the item in Web',
      activate_tv: true,
      export_account: true,
      delete_account: true,
      change_password: true,
      player_type: 'custom',
    },
    ios: {
      app_store_link: 'https://apps.apple.com/us/app/katon-play/id6760002737',
      is_client_active: true,
      latest_version: '1.0',
      force_update: true,
      show_buy_button: true,
      show_buy_button_alert: false,
      disable_buy_button: false,
      subscription_alert_msg: 'Please subscribe the item in Web',
      is_share_enabled: true,
      activate_tv: false,
      export_account: false,
      delete_account: true,
      change_password: true,
      player_type: 'default',
      on_boarding_content: {
        screen_1: {
          title: 'Better Streaming 123',
          sub_title: 'Choose your streaming quality with a wide range of quality options from 480p to 4k.',
          image: 'https://dev-play.katon360.com/images/config_assets/landing1.png',
        },
        screen_2: {
          title: 'Movies & TV Series',
          sub_title: 'Access to 10000+ movies & series in a standard subscription plan.',
          image: 'https://dev-play.katon360.com/images/config_assets/landing2.png',
        },
        screen_3: {
          title: 'AI Recommendation',
          sub_title: 'Getting confused what to watch, we got you on that with AI recommendation.',
          image: 'https://dev-play.katon360.com/images/config_assets/landing3.png',
        },
      },
    },
    tvos: {
      is_client_active: true,
      latest_version: '1.0',
      force_update: true,
      show_buy_button: true,
      show_buy_button_alert: false,
      disable_buy_button: false,
      subscription_alert_msg: 'Please subscribe the item in Web',
      activate_tv: true,
      export_account: false,
      delete_account: true,
      change_password: true,
      player_type: 'custom',
    },
    roku: {
      isAutoPlayEnabled: true,
      isServerSideAdCountDown: false,
      is_client_active: true,
      is_authentication_enabled: true,
      is_qr_authentication_enabled: true,
      is_favorite_enabled: true,
      is_continue_watching_enabled: false,
      is_onboarding_screen_enabled: true,
      is_guest_mode_enabled: true,
      latest_version: '1.0',
      force_update: true,
      show_buy_button: true,
      show_buy_button_alert: false,
      feature_flags: {
        ad_enabled: true,
      },
      disable_buy_button: false,
      subscription_alert_msg: 'Please subscribe the item in Web',
      activate_tv: true,
      export_account: false,
      delete_account: true,
      change_password: true,
      player_type: 'default',
      epgSettings: {
        catchUpHours: 6,
        epgContentInHours: 2,
        loadingDataText: '',
        numRows: 6,
        channelRightPadding: 4,
        fillEmptyContent: true,
        showCatchUpPrograms: true,
        timeBarLabelColor: '#FFFFFF',
        catchUpProgramBackgroundColor: '#FFFFFF80',
        programTitleColor: '#FFFFFF',
        programTitleFocusedColor: '#FFFFFF',
        ad_settings: {
          ad_config_level: 'global',
          ad_delivery_method: 'ssai',
          ad_delivery_provider: 'jwp',
        },
      },
      onBoarding: [
        {
          image_uri: 'https://dev-play.katon360.com/images/config_assets/landing1.png',
          page_title: 'Better Streaming',
          page_description: 'Choose your streaming quality with a wide range of quality options from 480p to 4k.',
        },
        {
          image_uri: 'https://dev-play.katon360.com/images/config_assets/landing2.png',
          page_title: 'Movies & TV Series',
          page_description: 'Access to 10000+ movies & series in a standard subscription plan.',
        },
        {
          image_uri: 'https://dev-play.katon360.com/images/config_assets/landing3.png',
          page_title: 'AI Recommendation',
          page_description: 'Getting confused what to watch, we got you on that with AI recommendation.',
        },
      ],
    },
  },
} as const;

export type RemoteConfig = typeof remoteConfig;
